'use client';

import { usePathname } from 'next/navigation';
import { useState, type FormEvent } from 'react';
import type { CreateInquiryInput, InquiryType } from '@vuganawe/types';
import {
  AUDIENCE_LABELS,
  AUDIENCE_TYPES,
  INQUIRY_TYPE_LABELS,
  LEARNING_MODES,
  LEARNING_MODE_LABELS,
} from '@vuganawe/types';
import { track } from '@/lib/analytics';
import { submitInquiry } from '@/lib/api/public';
import { FormField, SelectField, TextAreaField } from '../ui/fields';

interface InquiryFormProps {
  /** Fixes the inquiry type, or lets the visitor choose when omitted. */
  fixedType?: InquiryType;
  /** Preselects the type but keeps the selector visible (ignored with fixedType). */
  initialType?: InquiryType;
  /** Program titles for the interest selector. */
  programOptions: string[];
  submitLabel?: string;
  messageLabel?: string;
  messagePlaceholder?: string;
}

const TYPE_OPTIONS = (['GENERAL', 'CONSULTATION', 'PLACEMENT_TEST', 'CORPORATE'] as const).map(
  (value) => ({ value, label: INQUIRY_TYPE_LABELS[value] }),
);

export function InquiryForm({
  fixedType,
  initialType,
  programOptions,
  submitLabel = 'Send message',
  messageLabel = 'Your message',
  messagePlaceholder = 'Tell us your goal — for example: “I want to pass job interviews in English.”',
}: InquiryFormProps) {
  const pathname = usePathname();
  const [type, setType] = useState<InquiryType>(fixedType ?? initialType ?? 'GENERAL');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [serverError, setServerError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const isCorporate = type === 'CORPORATE';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const name = String(data.get('name') ?? '').trim();
    const email = String(data.get('email') ?? '').trim();
    const message = String(data.get('message') ?? '').trim();
    const companyName = String(data.get('companyName') ?? '').trim();

    const errors: Record<string, string> = {};
    if (name.length < 2) errors.name = 'Please tell us your name.';
    if (!/^\S+@\S+\.\S+$/.test(email)) errors.email = 'Please enter a valid email address.';
    if (message.length < 5) errors.message = 'A short sentence about your goal helps us help you.';
    if (isCorporate && !companyName) errors.companyName = 'Please add your company name.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload: CreateInquiryInput = {
      type,
      name,
      email,
      phone: String(data.get('phone') ?? '').trim() || undefined,
      message,
      programInterest: String(data.get('programInterest') ?? '') || undefined,
      audience: (String(data.get('audience') ?? '') || undefined) as CreateInquiryInput['audience'],
      preferredMode: (String(data.get('preferredMode') ?? '') ||
        undefined) as CreateInquiryInput['preferredMode'],
      sourcePage: pathname ?? undefined,
      companyName: isCorporate ? companyName : undefined,
      teamSize: isCorporate && data.get('teamSize') ? Number(data.get('teamSize')) : undefined,
    };

    setStatus('submitting');
    setServerError('');
    try {
      await submitInquiry(payload);
      track('FORM_SUBMIT', pathname ?? '/', `inquiry-${type.toLowerCase()}`);
      setStatus('success');
      form.reset();
    } catch (error) {
      setStatus('error');
      setServerError(
        error instanceof Error
          ? error.message
          : 'We could not send your message. Please try again.',
      );
    }
  }

  if (status === 'success') {
    return (
      <div className="vn-alert vn-alert--success" role="status">
        <strong>Thank you — we received your message.</strong>
        <br />
        We usually reply within one working day. If it is urgent, call or WhatsApp us directly.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {serverError ? (
        <div className="vn-alert vn-alert--error" role="alert">
          {serverError}
        </div>
      ) : null}

      {!fixedType ? (
        <SelectField
          id="inquiry-type"
          label="What is this about?"
          options={TYPE_OPTIONS.map((option) => ({ ...option }))}
          value={type}
          onChange={(event) => setType(event.target.value as InquiryType)}
        />
      ) : null}

      <div className="vn-grid vn-grid--2">
        <FormField id="name" name="name" label="Your name" required error={fieldErrors.name} autoComplete="name" />
        <FormField
          id="email"
          name="email"
          type="email"
          label="Email"
          required
          error={fieldErrors.email}
          autoComplete="email"
        />
      </div>
      <div className="vn-grid vn-grid--2">
        <FormField
          id="phone"
          name="phone"
          type="tel"
          label="Phone / WhatsApp"
          hint="Optional, but the fastest way for us to reach you."
          autoComplete="tel"
          placeholder="+250 7xx xxx xxx"
        />
        <SelectField
          id="programInterest"
          name="programInterest"
          label="Program of interest"
          placeholder="Not sure yet — recommend for me"
          options={programOptions.map((title) => ({ value: title, label: title }))}
        />
      </div>

      {isCorporate ? (
        <div className="vn-grid vn-grid--2">
          <FormField
            id="companyName"
            name="companyName"
            label="Company name"
            required
            error={fieldErrors.companyName}
          />
          <FormField
            id="teamSize"
            name="teamSize"
            type="number"
            min={1}
            label="Team size (approx.)"
          />
        </div>
      ) : (
        <div className="vn-grid vn-grid--2">
          <SelectField
            id="audience"
            name="audience"
            label="Who is the training for?"
            placeholder="Select…"
            options={AUDIENCE_TYPES.map((value) => ({ value, label: AUDIENCE_LABELS[value] }))}
          />
          <SelectField
            id="preferredMode"
            name="preferredMode"
            label="Preferred learning mode"
            placeholder="No preference"
            options={LEARNING_MODES.map((value) => ({ value, label: LEARNING_MODE_LABELS[value] }))}
          />
        </div>
      )}

      <TextAreaField
        id="message"
        name="message"
        label={messageLabel}
        required
        error={fieldErrors.message}
        placeholder={messagePlaceholder}
      />

      <button type="submit" className="vn-btn vn-btn--primary" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending…' : submitLabel}
      </button>
    </form>
  );
}
